import { Children, cloneElement, isValidElement, useId } from 'react';
import type { ComponentProps, ReactNode } from 'react';

import type { ButtonVariant } from '@playbook/atoms/button';
import { FormLabel } from '@playbook/atoms/form-label';
import { TextInput } from '@playbook/atoms/inputs/text-input';
import type { Size } from '@playbook/utils';

import css from './styles.module.css';

export type InputGroupSize = Extract<Size, 'sm' | 'md'>;

const DefaultInputGroupSize: InputGroupSize = 'md' as const;

/** Figma draws a button in a group in one look only. */
const InputGroupButtonVariant: ButtonVariant = 'tertiary' as const;

/** The props the group owns and overwrites on every segment. */
interface InputGroupSegmentProperties {
	variant?: ButtonVariant;
	size?: InputGroupSize;
}

type InputGroupEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface InputGroupDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface InputGroupProperties
	extends InputGroupEventHandlers, InputGroupDataAttributes {
	size?: InputGroupSize;

	/** Keeps `label` as the group's accessible name without drawing it. */
	hideLabel?: boolean;

	label: string;
	description?: string;
	help?: string;

	children: ReactNode;
}

/**
 *	Joins text inputs, buttons, or both into one row. One component covers
 *	every mix, since the only difference is what is passed in.
 *
 *	The group writes `size` onto every segment, and `variant="tertiary"` onto
 *	every segment that is not a text input, because segments that disagree
 *	read as separate controls that happen to touch. Anything that is not a
 *	`TextInput` counts as a button, so a component wrapping `Button` gets the
 *	props too.
 *
 *	The label names the group, not one field, so it is wired through
 *	`aria-labelledby` on the `role="group"` rather than `htmlFor`. `useId` is
 *	only there to build that id, and holds no state.
 */
export function InputGroup({
	size = DefaultInputGroupSize,
	hideLabel = false,

	label,
	description,
	help,

	children,

	...props
}: InputGroupProperties): ReactNode {
	const id: string = useId();

	const segments: ReactNode = Children.map(children, (child) => {
		if (!isValidElement<InputGroupSegmentProperties>(child)) {
			return child;
		}

		const isButton: boolean = child.type !== TextInput;

		if (
			child.props.size !== undefined ||
			(isButton && child.props.variant !== undefined)
		) {
			console.warn(
				'InputGroup sets `size` for every segment, and `variant` for every button. Set `size` on the group; the ones on its children are ignored.'
			);
		}

		return cloneElement(
			child,
			isButton ? { size, variant: InputGroupButtonVariant } : { size }
		);
	});

	return (
		<div
			{...props}
			role="group"
			aria-label={hideLabel ? label : undefined}
			aria-labelledby={hideLabel ? undefined : `${id}-label`}
			aria-describedby={
				!hideLabel && description ? `${id}-description` : undefined
			}
			className={css.group}>
			{!hideLabel && (
				<FormLabel
					htmlFor={id}
					size={size}
					label={label}
					description={description}
					help={help}
				/>
			)}
			<div className={css.row}>{segments}</div>
		</div>
	);
}
